import React from "react";

// The capability picker — extracted from CustomRoleEditor (Sub-project E2,
// Task 6) so both CustomRoleEditor (view/edit of existing roles) and
// OrgRoleWizard's "Review capabilities" step share the exact same grouped
// checkbox-list UI instead of drifting on two copies.

// Groups the capability catalog by `group` (falls back to "other"),
// alphabetically sorted by group key.
export function groupCapabilitiesByGroup(capabilityCatalog = []) {
  const byGroup = new Map();
  for (const cap of capabilityCatalog) {
    const g = cap.group || "other";
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g).push(cap);
  }
  return Array.from(byGroup.entries()).sort(([a], [b]) => a.localeCompare(b));
}

// One capability row: checkbox (or, in read-only mode, a plain check glyph)
// plus the capability's name and description. `annotation` is an optional
// node rendered inline next to the name — OrgRoleWizard's diff step uses it
// to flag "Added"/sensitive capabilities; CustomRoleEditor doesn't pass one.
export function CapabilityRow({ cap, checked, onToggle, readOnly, annotation }) {
  return (
    <label
      className={`flex items-start gap-2.5 py-1.5 ${readOnly ? "" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={readOnly ? undefined : () => onToggle(cap.code)}
        disabled={readOnly}
        aria-label={cap.name}
        className="mt-0.5 rounded border-border accent-primary shrink-0"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="block text-sm text-text">{cap.name}</span>
          {annotation}
        </span>
        {cap.description && <span className="block text-xs text-text-2">{cap.description}</span>}
      </span>
    </label>
  );
}

// The full grouped list. `renderAnnotation(code)` (optional) returns a node
// to render next to a given capability's name.
export function CapabilityPickerList({ capabilityCatalog, selectedCaps, onToggle, readOnly, renderAnnotation }) {
  const grouped = React.useMemo(() => groupCapabilitiesByGroup(capabilityCatalog), [capabilityCatalog]);
  return (
    <div className="divide-y divide-border">
      {grouped.map(([groupKey, caps]) => (
        <div key={groupKey} className="py-3 first:pt-0 last:pb-0">
          <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-1 capitalize">
            {groupKey}
          </div>
          {caps.map((cap) => (
            <CapabilityRow
              key={cap.code}
              cap={cap}
              checked={selectedCaps.includes(cap.code)}
              onToggle={onToggle}
              readOnly={readOnly}
              annotation={renderAnnotation ? renderAnnotation(cap.code) : null}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
